import React, { useEffect, useState } from "react";
import { X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SlidePanelProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  width?: string;
  children: React.ReactNode;
  className?: string;
}

export function SlidePanel({ open, onClose, title, width = "w-[480px]", children, className }: SlidePanelProps) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    } else {
      setVisible(false);
      const timer = setTimeout(() => setMounted(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  if (!mounted) return null;

  return (
    <div
      className={cn(
        "flex-shrink-0 flex flex-col bg-card border border-border rounded-2xl overflow-hidden transition-all duration-200 ease-out",
        visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4",
        width,
        className,
      )}
    >
      {/* header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="text-lg font-semibold truncate">{title || ""}</h3>
        <Button variant="ghost" size="icon-sm" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>
      {/* body */}
      <div className="flex-1 overflow-y-auto p-5">{children}</div>
    </div>
  );
}
